import React, { useState } from 'react';
import { Facebook, Twitter, Link as LinkIcon, Share2, Check } from 'lucide-react';
import { track } from '../lib/analytics';
import { socialImageUrl } from '../lib/images';

interface ShareButtonsProps {
  /** Absolute URL of the listing (canonical slug URL). */
  url: string;
  title: string;
  imageUrl?: string;
  /** Where the share row is rendered, for analytics (e.g. 'listing_detail'). */
  context?: string;
  className?: string;
}

/**
 * One-click share row. Uses plain share-dialog URLs (no SDKs, no API keys) —
 * the link unfurls with photo + price via the existing per-listing OG tags.
 */
function ShareButtons({ url, title, imageUrl, context = 'listing', className = '' }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const open = (channel: string, shareUrl: string) => {
    track('share', { method: channel, content_type: 'listing', context });
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=500');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      track('share', { method: 'copy_link', content_type: 'listing', context });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  const handleNative = async () => {
    try {
      await navigator.share({ url, title });
      track('share', { method: 'native', content_type: 'listing', context });
    } catch {
      // user dismissed the sheet
    }
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const pinImage = encodeURIComponent(socialImageUrl(imageUrl) || '');

  const btn =
    'inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button
        onClick={() => open('facebook', `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}
        className={btn}
        aria-label="Share on Facebook"
      >
        <Facebook className="h-4 w-4 text-[#1877F2]" />
        Facebook
      </button>
      <button
        onClick={() =>
          open('pinterest', `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${pinImage}&description=${encodedTitle}`)
        }
        className={btn}
        aria-label="Share on Pinterest"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#E60023" aria-hidden="true">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.08 3.16 9.43 7.63 11.18-.11-.95-.2-2.4.04-3.44.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.66.96-2.9 2.16-2.9 1.02 0 1.51.77 1.51 1.68 0 1.03-.65 2.56-.99 3.98-.28 1.19.6 2.16 1.77 2.16 2.13 0 3.77-2.25 3.77-5.49 0-2.87-2.06-4.88-5.01-4.88-3.41 0-5.42 2.56-5.42 5.21 0 1.03.4 2.14.89 2.74.1.12.11.22.08.34l-.33 1.36c-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.74-7.25 7.91-7.25 4.15 0 7.38 2.96 7.38 6.92 0 4.13-2.6 7.45-6.21 7.45-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15A12 12 0 1 0 12 0z" />
        </svg>
        Pinterest
      </button>
      <button
        onClick={() => open('x', `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)}
        className={btn}
        aria-label="Share on X"
      >
        <Twitter className="h-4 w-4" />
        X
      </button>
      <button onClick={handleCopy} className={btn} aria-label="Copy link">
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <LinkIcon className="h-4 w-4" />}
        {copied ? 'Copied' : 'Copy link'}
      </button>
      {typeof navigator !== 'undefined' && 'share' in navigator && (
        <button onClick={handleNative} className={btn} aria-label="More share options">
          <Share2 className="h-4 w-4" />
          More
        </button>
      )}
    </div>
  );
}

export default ShareButtons;
